"use strict";
/**
 * Sync foreign-entity lookup for script helpers (entity libraries Phase 4).
 * Backend fills this from library home DB reads before scene/condition evaluation
 * (same pattern as FriendsService cache warm).
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.InMemoryForeignEntityLookup = void 0;
/**
 * In-memory lookup for engine unit tests (and optional host seeding).
 */
class InMemoryForeignEntityLookup {
    constructor() {
        this.byKey = new Map();
        this.defaultOwnerUserId = null;
        this.allowedOwnerUserIds = null;
    }
    setDefaultOwnerUserId(userId) {
        this.defaultOwnerUserId = userId ? String(userId).trim() || null : null;
    }
    /**
     * When set, only these owners may be queried. `null` = allow any owner present in cache.
     */
    setAllowedOwnerUserIds(userIds) {
        if (userIds === null) {
            this.allowedOwnerUserIds = null;
            return;
        }
        this.allowedOwnerUserIds = new Set([...userIds].map(id => String(id).trim()).filter(Boolean));
    }
    clear() {
        this.byKey.clear();
    }
    put(adventureId, ownerUserId, snapshot) {
        const adv = String(adventureId ?? '').trim();
        const owner = String(ownerUserId ?? '').trim();
        const entityId = String(snapshot.entityId ?? '').trim();
        if (!adv || !owner || !entityId)
            return;
        this.byKey.set(this.key(adv, entityId, owner), {
            ...snapshot,
            entityId,
            typeId: String(snapshot.typeId ?? '').trim(),
            displayName: String(snapshot.displayName ?? ''),
            variables: { ...snapshot.variables }
        });
    }
    foreignEntityExists(adventureId, entityId, ownerUserId) {
        return this.resolve(adventureId, entityId, ownerUserId) !== null;
    }
    getForeignEntityDisplayName(adventureId, entityId, ownerUserId) {
        return this.resolve(adventureId, entityId, ownerUserId)?.displayName ?? '';
    }
    getForeignEntityVariable(adventureId, entityId, name, ownerUserId) {
        const snap = this.resolve(adventureId, entityId, ownerUserId);
        if (!snap)
            return undefined;
        const key = String(name ?? '').trim();
        if (!key)
            return undefined;
        return snap.variables[key];
    }
    listForeignEntitySnapshots(adventureId, ownerUserId, filter) {
        const adv = String(adventureId ?? '').trim();
        const owner = String(ownerUserId ?? this.defaultOwnerUserId ?? '').trim();
        if (!adv || !owner)
            return [];
        if (this.allowedOwnerUserIds && !this.allowedOwnerUserIds.has(owner)) {
            return [];
        }
        const typeFilter = filter?.types && filter.types.length > 0 ? new Set(filter.types) : null;
        const prefix = `${owner}::${adv}::`;
        const out = [];
        for (const [k, snap] of this.byKey) {
            if (!k.startsWith(prefix))
                continue;
            if (typeFilter && !typeFilter.has(snap.typeId))
                continue;
            out.push({ ...snap, variables: { ...snap.variables } });
        }
        out.sort((a, b) => a.displayName.localeCompare(b.displayName, undefined, { sensitivity: 'base' }));
        return out;
    }
    /** Drop all cached snapshots for one owner + library adventure (re-seed after mutate). */
    clearOwnerLibrary(adventureId, ownerUserId) {
        const adv = String(adventureId ?? '').trim();
        const owner = String(ownerUserId ?? '').trim();
        if (!adv || !owner)
            return;
        const prefix = `${owner}::${adv}::`;
        for (const k of [...this.byKey.keys()]) {
            if (k.startsWith(prefix)) {
                this.byKey.delete(k);
            }
        }
    }
    resolve(adventureId, entityId, ownerUserId) {
        const adv = String(adventureId ?? '').trim();
        const id = String(entityId ?? '').trim();
        const owner = String(ownerUserId ?? this.defaultOwnerUserId ?? '').trim();
        if (!adv || !id || !owner)
            return null;
        if (this.allowedOwnerUserIds && !this.allowedOwnerUserIds.has(owner)) {
            return null;
        }
        return this.byKey.get(this.key(adv, id, owner)) ?? null;
    }
    key(adventureId, entityId, ownerUserId) {
        return `${ownerUserId}::${adventureId}::${entityId}`;
    }
}
exports.InMemoryForeignEntityLookup = InMemoryForeignEntityLookup;
//# sourceMappingURL=ForeignEntityLookup.js.map